import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store, createSelector } from '@ngrx/store';

import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';

import { LoadingState } from 'src/app/utilities/state.util';

import { maskEpi } from '../bluesage.util';

import { BlueSageTerminalPickerActions } from './bluesage-terminal-picker.actions';
import { blueSageTerminalPickerFeature } from './bluesage-terminal-picker.feature';
import {
    selectFilteredTerminals,
    selectHiddenTerminalCount,
    selectVisibleTerminals,
} from './bluesage-terminal-picker.selectors';
import { BlueSageTerminal } from './bluesage-terminal-picker.state';

/** How a row reads to an operator, and whether Send may be pressed on it. */
interface TerminalRow {
    epi: string;
    /** `••••7943` — the trailing digits are what is printed on the device. */
    masked: string;
    status: string;
    /** Plain language, never the raw enum. */
    statusLabel: string;
    /** Dimmed covers offline AND unknown; neither is selectable-blocking. */
    dimmed: boolean;
    selected: boolean;
    /** True when this CRM already has a live push on the device. */
    busy: boolean;
    /** *In use — job KC-1234*, or the reason Send is unavailable. */
    busyLabel: string | undefined;
    /** Shown above the first recent row only. */
    recent: boolean;
}

/**
 * The terminal picker.
 *
 * Shared by every surface that puts money on a reader. Three rules drive the
 * whole component and each of them was a decision, not a default:
 *
 *  1. **EPI only, no labels.** Devices move between trucks; EPIs persist. A
 *     stale label on a money screen reads as authoritative and is worse than
 *     none.
 *  2. **Offline and unknown are dimmed but SELECTABLE, and Send stays
 *     enabled.** The roster is live when the picker opens and a minute old when
 *     Send is pressed; the middleware's check at push time is the authority.
 *     The spec says so in as many words. `unknown` in particular does not mean
 *     the device is down — it means Valor's status service could not be
 *     reached.
 *  3. **Busy is the one thing that disables Send.** It is not a status reading:
 *     it is this CRM's own device lock, and it means a push we started is live
 *     on that reader right now.
 *
 * The component emits the chosen EPI rather than pushing anything itself. What
 * to do with a terminal is the dialog's business; finding one is this
 * component's.
 */
@Component({
    selector: 'app-bluesage-terminal-picker',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        InputTextModule,
        TooltipModule,
    ],
    templateUrl: './bluesage-terminal-picker.component.html',
    styleUrls: [ './bluesage-terminal-picker.component.scss' ],
})
export class BlueSageTerminalPickerComponent implements OnInit {

    actions = BlueSageTerminalPickerActions;

    viewModel$ = this.store.select(createSelector({
        rows: createSelector(
            selectVisibleTerminals,
            blueSageTerminalPickerFeature.selectSelectedEpi,
            blueSageTerminalPickerFeature.selectRecentEpis,
            (terminals, selectedEpi, recentEpis) =>
                terminals.map((terminal) => toRow(terminal, selectedEpi, recentEpis)),
        ),

        /** How many the shortlist is holding back. Zero hides the expander. */
        hiddenCount: selectHiddenTerminalCount,
        expanded: blueSageTerminalPickerFeature.selectExpanded,

        /** When the roster was read, so nobody reads a minute-old dot as live truth. */
        checkedAt: createSelector(
            blueSageTerminalPickerFeature.selectTerminals,
            (terminals) => terminals[0]?.statusCheckedAt ?? undefined,
        ),
        rosterIsCached: createSelector(
            blueSageTerminalPickerFeature.selectTerminals,
            (terminals) => terminals[0]?.statusSource === 'cached',
        ),

        loading: createSelector(
            blueSageTerminalPickerFeature.selectLoading,
            (loading) => loading === LoadingState.LOADING,
        ),
        loadFailed: blueSageTerminalPickerFeature.selectLoadFailed,

        /**
         * Nothing to show, and it is NOT a failure — the distinction the review
         * queue got wrong. A failed load must never render as "this location has
         * no terminals".
         */
        showEmptyState: createSelector(
            blueSageTerminalPickerFeature.selectTerminals,
            blueSageTerminalPickerFeature.selectLoading,
            blueSageTerminalPickerFeature.selectLoadFailed,
            (terminals, loading, loadFailed) =>
                terminals.length === 0 && loading === LoadingState.LOADED && !loadFailed,
        ),

        /** Typed a filter that matches nothing — a different message entirely. */
        showNoMatches: createSelector(
            blueSageTerminalPickerFeature.selectTerminals,
            selectFilteredTerminals,
            (terminals, filtered) => terminals.length > 0 && filtered.length === 0,
        ),

        search: blueSageTerminalPickerFeature.selectSearch,
    }));

    constructor(
        public store: Store,
    ) {}

    ngOnInit(): void {
        this.store.dispatch(BlueSageTerminalPickerActions.pickerOpened());
    }
}

function toRow(
    terminal: BlueSageTerminal,
    selectedEpi: string | undefined,
    recentEpis: string[],
): TerminalRow {
    const busy = Boolean(terminal.busy);

    return {
        epi: terminal.epi,
        masked: maskEpi(terminal.epi),
        status: terminal.status,
        statusLabel: statusLabel(terminal.status),
        dimmed: terminal.status !== 'online',
        selected: terminal.epi === selectedEpi,
        busy,
        busyLabel: busy
            ? (terminal.busy?.jobCode
                ? `In use — job ${ terminal.busy.jobCode }`
                : 'In use — another payment')
            : undefined,
        recent: recentEpis.includes(terminal.epi),
    };
}

/**
 * Plain language, and `unknown` never rendered as "offline".
 *
 * Saying "offline" about a device we simply could not ask about is the exact
 * mistake `502 terminal_status_unknown` was introduced to stop us making.
 */
function statusLabel(status: string): string {
    switch (status) {
        case 'online': return 'Online';
        case 'offline': return 'Offline';
        default: return 'Status unknown';
    }
}
